import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = {
    class: "flex items-center justify-center bg-gray-100 p-4 sm:p-6 lg:p-8",
    style: { "flex": "1" }
};
const _hoisted_2 = { class: "max-w-xs sm:max-w-md lg:max-w-lg xl:max-w-xl w-full bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg sm:shadow-xl lg:shadow-2xl p-6 sm:p-8 lg:p-10" };
const _hoisted_3 = { class: "text-center" };
const _hoisted_4 = { class: "text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 lg:mb-6" };
const _hoisted_5 = { class: "mb-4 sm:mb-6 lg:mb-8" };
const _hoisted_6 = { class: "text-sm sm:text-base lg:text-lg text-gray-600 mb-3 sm:mb-4 lg:mb-6 leading-relaxed" };
const _hoisted_7 = {
    key: 0,
    class: "bg-gray-50 rounded-lg p-3 sm:p-4 lg:p-6 mb-3 sm:mb-4 lg:mb-6"
};
const _hoisted_8 = { class: "text-xs sm:text-sm lg:text-base font-medium text-gray-700 mb-2 lg:mb-3" };
const _hoisted_9 = { class: "text-xs sm:text-sm lg:text-base text-gray-600 font-mono break-words" };
const _hoisted_10 = {
    key: 1,
    class: "text-xs sm:text-sm lg:text-base text-gray-500"
};
const _hoisted_11 = {
    key: 0,
    class: "space-y-2 sm:space-y-3 lg:space-y-4"
};
const _hoisted_12 = ["onClick"];
const _hoisted_13 = ["onClick"];
const _hoisted_14 = ["href", "onClick"];
const _hoisted_15 = {
    key: 1,
    class: "space-y-2 sm:space-y-3 lg:space-y-4"
};
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useErrorStore } from "../../stores/errorStore";
import { useLogger } from "@/shared/composables/useLogger";
import { isExternalUrl, toInternalPath } from "@/shared/utils/urlUtils";
// Router composables
export default /*@__PURE__*/ _defineComponent({
    __name: 'Error',
    setup(__props) {
        const route = useRoute();
        const router = useRouter();
        // i18n
        const { t } = useI18n();
        // Logger
        const logger = useLogger();
        // Error store
        const errorStore = useErrorStore();
        // Computed properties based on error store
        // Check if custom buttons are defined
        const hasCustomButtons = computed(() => {
            return errorStore.currentError?.buttons && errorStore.currentError.buttons.length > 0;
        });
        // Get custom buttons from error state
        const customButtons = computed(() => {
            return errorStore.currentError?.buttons || [];
        });
        // Check if details should be hidden
        const hideDetails = computed(() => {
            return errorStore.currentError?.hideDetails === true;
        });
        const errorTitle = computed(() => {
            if (!errorStore.currentError)
                return t("error.title.default");
            // Use custom title key if provided
            if (errorStore.currentError.titleKey) {
                return t(errorStore.currentError.titleKey);
            }
            switch (errorStore.currentError.type) {
                case "NETWORK_ERROR":
                    return t("error.title.networkError");
                case "AUTHENTICATION_ERROR":
                    return t("error.title.authenticationError");
                case "AUTHORIZATION_ERROR":
                    return t("error.title.authorizationError");
                case "SERVER_ERROR":
                    return t("error.title.serverError");
                case "API_ERROR":
                    return t("error.title.apiError");
                case "VALIDATION_ERROR":
                    return t("error.title.validationError");
                case "PAYMENT_ERROR":
                    return t("error.title.default");
                default:
                    return t("error.title.default");
            }
        });
        const errorMessage = computed(() => {
            const currentError = errorStore.currentError;
            // If error has a translation key, use it for dynamic language switching
            if (currentError?.translationKey) {
                return t(currentError.translationKey, currentError.translationParams || {});
            }
            // Otherwise, use the stored message (for custom error messages)
            return currentError?.message || t("error.message.default");
        });
        const errorDetails = computed(() => {
            return errorStore.currentError?.context || "";
        });
        const errorCode = computed(() => {
            return errorStore.currentError?.code || "";
        });
        const showRetryButton = ref(false);
        // Parse error information from route query or props
        onMounted(() => {
            // Check if we should show retry button based on query params
            const queryRetry = route.query.retry;
            showRetryButton.value = queryRetry === "true";
            // Check if we're explicitly staying on error page (from API error navigation)
            const stayOnErrorPage = sessionStorage.getItem("stay-on-error-page");
            // If no error in store and no query params and we're not explicitly staying, redirect to /dashboard
            if (!errorStore.currentError && !route.query.error && !stayOnErrorPage) {
                router.push("/dashboard");
            }
            // Clean up the flag if it exists
            if (stayOnErrorPage) {
                sessionStorage.removeItem("stay-on-error-page");
            }
        });
        // Handle retry action
        const handleRetry = () => {
            // Clear current error from store
            errorStore.clearCurrentError();
            const queryRetryUrl = route.query.retryUrl;
            if (queryRetryUrl) {
                router.push(queryRetryUrl);
            }
            else {
                // Default retry behavior - go back to previous page
                goBack();
            }
        };
        // Handle button click based on action type
        const handleButtonClick = (btn) => {
            switch (btn.action) {
                case "retry":
                    // Clear error before navigation for retry
                    errorStore.clearCurrentError();
                    // Use button URL if provided, otherwise go back
                    if (btn.url && btn.url.trim() !== "") {
                        router.push(btn.url).catch((err) => {
                            logger.debug("Failed to navigate to retry URL:", err);
                            goBack();
                        });
                    }
                    else {
                        goBack();
                    }
                    break;
                case "back":
                    goBack();
                    break;
                case "cancel":
                    // For external navigation, no need to clear - page will reload
                    if (btn.url && btn.url.trim() !== "") {
                        window.location.href = btn.url;
                    }
                    else {
                        goBack();
                    }
                    break;
                case "link":
                    if (btn.url && btn.url.trim() !== "") {
                        // Same-origin URLs go through the router so Pinia state survives.
                        if (isExternalUrl(btn.url)) {
                            window.location.href = btn.url;
                        }
                        else {
                            errorStore.clearCurrentError();
                            router.push(toInternalPath(btn.url)).catch((err) => {
                                logger.debug("Failed to navigate to internal URL:", err);
                                goBack();
                            });
                        }
                    }
                    else {
                        goBack();
                    }
                    break;
            }
        };
        // Handle link click for <a> tag styled buttons
        const handleLinkClick = (event, btn) => {
            // Same-origin URLs go through the router so Pinia state survives.
            if (btn.url && !isExternalUrl(btn.url)) {
                event.preventDefault();
                errorStore.clearCurrentError();
                router.push(toInternalPath(btn.url)).catch((err) => {
                    logger.debug("Failed to navigate to internal URL:", err);
                    goBack();
                });
            }
            // For external URLs, let the default <a> behavior handle it (page will reload)
            // No need to clear errorStore as the page will reload anyway
        };
        // Go back to previous page
        const goBack = () => {
            // Clear current error from store
            errorStore.clearCurrentError();
            if (window.history.length > 1) {
                router.go(-1);
            }
            else {
                router.push("/");
            }
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _cache[0] || (_cache[0] = _createElementVNode("div", { class: "mb-4 sm:mb-6 lg:mb-8" }, [
                            _createElementVNode("div", { class: "w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 lg:mb-6" }, [
                                _createElementVNode("svg", {
                                    class: "w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-red-600",
                                    fill: "none",
                                    stroke: "currentColor",
                                    viewBox: "0 0 24 24",
                                    xmlns: "http://www.w3.org/2000/svg"
                                }, [
                                    _createElementVNode("path", {
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round",
                                        "stroke-width": "2",
                                        d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    })
                                ])
                            ])
                        ], -1 /* CACHED */)),
                        _createElementVNode("h1", _hoisted_4, _toDisplayString(errorTitle.value), 1 /* TEXT */),
                        _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("p", _hoisted_6, _toDisplayString(errorMessage.value), 1 /* TEXT */),
                            (errorDetails.value && !hideDetails.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                    _createElementVNode("h3", _hoisted_8, _toDisplayString(_unref(t)("error.details")), 1 /* TEXT */),
                                    _createElementVNode("p", _hoisted_9, _toDisplayString(errorDetails.value), 1 /* TEXT */)
                                ]))
                                : _createCommentVNode("v-if", true),
                            (errorCode.value && !hideDetails.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(_unref(t)("error.code")) + " " + _toDisplayString(errorCode.value), 1 /* TEXT */))
                                : _createCommentVNode("v-if", true)
                        ]),
                        (hasCustomButtons.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(customButtons.value, (btn, index) => {
                                    return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                                        (btn.style === 'primary')
                                            ? (_openBlock(), _createElementBlock("button", {
                                                key: 0,
                                                class: _normalizeClass(["w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 sm:py-3 lg:py-4 px-4 sm:px-6 lg:px-8 rounded-lg transition-colors duration-200 text-sm sm:text-base lg:text-lg shadow-md", { uppercase: btn.uppercase }]),
                                                onClick: ($event) => (handleButtonClick(btn))
                                            }, _toDisplayString(_unref(t)(btn.labelKey)), 11 /* TEXT, CLASS, PROPS */, _hoisted_12))
                                            : (btn.style === 'secondary')
                                                ? (_openBlock(), _createElementBlock("button", {
                                                    key: 1,
                                                    class: _normalizeClass(["w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 sm:py-3 lg:py-4 px-4 sm:px-6 lg:px-8 rounded-lg transition-colors duration-200 text-sm sm:text-base lg:text-lg shadow-md", { uppercase: btn.uppercase }]),
                                                    onClick: ($event) => (handleButtonClick(btn))
                                                }, _toDisplayString(_unref(t)(btn.labelKey)), 11 /* TEXT, CLASS, PROPS */, _hoisted_13))
                                                : (btn.style === 'link' && btn.url && btn.url.trim() !== '')
                                                    ? (_openBlock(), _createElementBlock("a", {
                                                        key: 2,
                                                        href: btn.url,
                                                        class: _normalizeClass(["block w-full text-center text-red-600 underline font-medium py-2 sm:py-3 lg:py-4 px-4 sm:px-6 lg:px-8 transition-colors duration-200 text-sm sm:text-base lg:text-lg hover:text-red-700", { uppercase: btn.uppercase }]),
                                                        onClick: ($event) => (handleLinkClick($event, btn))
                                                    }, _toDisplayString(_unref(t)(btn.labelKey)), 11 /* TEXT, CLASS, PROPS */, _hoisted_14))
                                                    : _createCommentVNode("v-if", true)
                                    ], 64 /* STABLE_FRAGMENT */));
                                }), 128 /* KEYED_FRAGMENT */))
                            ]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                (showRetryButton.value)
                                    ? (_openBlock(), _createElementBlock("button", {
                                        key: 0,
                                        class: "w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 sm:py-3 lg:py-4 px-4 sm:px-6 lg:px-8 rounded-lg transition-colors duration-200 text-sm sm:text-base lg:text-lg",
                                        onClick: handleRetry
                                    }, _toDisplayString(_unref(t)("error.actions.tryAgain")), 1 /* TEXT */))
                                    : _createCommentVNode("v-if", true),
                                _createElementVNode("button", {
                                    class: "w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 sm:py-3 lg:py-4 px-4 sm:px-6 lg:px-8 rounded-lg transition-colors duration-200 text-sm sm:text-base lg:text-lg",
                                    onClick: goBack
                                }, _toDisplayString(_unref(t)("error.actions.retry")), 1 /* TEXT */)
                            ]))
                    ])
                ])
            ]));
        };
    }
});
